import { useContext, useEffect, useRef, useState } from "react";

import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BulletinResource,
  createTagRequest,
  deleteBulletinAttachment,
  deleteBulletinPdf,
  patchTagRequest,
  postBulletinCreateBulletinUrlSignedParams,
  uploadBulletinAttachment,
  uploadBulletinPdf,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import LoadingModal from "@/common/LoadingModal";
import TriUploadFile from "@/common/TriUploadFile";
import { ImageEditor } from "@/components/alerts/add/ImageEditor";
import { ManageTags } from "@/components/alerts/add/ManageTags";
import { SingleAlertContext } from "@/components/alerts/context";
import upload from "@/images/upload.png";
import { AlertMode, IEvidenceFile, IPdfFile } from "@/types";
import "@/components/alerts/alerts.scss";

import { SortableAttachments } from "./SortableAttachments";
import styles from "./UploadStep.module.css";

interface UploadStepProps {
  bulletinID: number;
  mode: AlertMode;
  imagesLoading: boolean;
  onAttachmentSyncingChange?: (syncing: boolean) => void;
}

export const UploadStep = ({ mode, ...props }: UploadStepProps) => {
  const [isPdfUploading, setIsPDFUploading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isAttachmentsSyncing, setIsAttachmentsSyncing] = useState(false);
  const [shouldCancel, setShouldCancel] = useState(false);
  const shouldCancelRef = useRef(shouldCancel);
  const [editMediaObject, setEditMediaObject] = useState(null);
  const { setAlertProperty, alertProperties } = useContext(SingleAlertContext);
  const { evidence_files, pdf } = alertProperties;
  const attachmentsFromDraft =
    "attachments" in alertProperties ? alertProperties.attachments : undefined;
  const tagsFromDraft =
    "tagsFromDraft" in alertProperties
      ? alertProperties.tagsFromDraft
      : undefined;
  const { t } = useTranslation("bulletin/create/upload");
  const pdfInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const attachmentsSyncedRef = useRef(false);
  const tagsSyncedRef = useRef(false);

  // When we have attachments from draft/query and bulletinID, call backend to merge into bulletin and get evidence_files
  useEffect(() => {
    if (
      !props.bulletinID ||
      !attachmentsFromDraft ||
      !Array.isArray(attachmentsFromDraft) ||
      attachmentsFromDraft.length === 0 ||
      attachmentsSyncedRef.current
    ) {
      return;
    }
    const validAttachments = attachmentsFromDraft.filter((a) => a.s3key);
    if (validAttachments.length === 0) {
      return;
    }
    const payload = {
      attachments: validAttachments.map((a) => ({
        filename: a.filename,
        content_type: a.content_type ?? "application/octet-stream",
        s3key: a.s3key!,
        sort_ordinal:
          typeof a.sort_ordinal === "number" ? a.sort_ordinal : undefined,
      })),
    };
    attachmentsSyncedRef.current = true;
    setIsAttachmentsSyncing(true);
    props.onAttachmentSyncingChange?.(true);
    postBulletinCreateBulletinUrlSignedParams({
      body: payload,
      path: { bulletin_id: props.bulletinID },
    })
      .then((res) => {
        setAlertProperty("evidence_files", {
          ...(res.data!.evidence_files as { [key: string]: IEvidenceFile }),
        });
        // Clear the draft attachments so this sync doesn't re-run if the
        // component remounts (e.g. navigating away and back). The ref alone
        // resets on remount, but the attachments persist in context.
        setAlertProperty("attachments", []);
      })
      .catch(() => {
        attachmentsSyncedRef.current = false;
      })
      .finally(() => {
        setIsAttachmentsSyncing(false);
        props.onAttachmentSyncingChange?.(false);
      });
  }, [props.bulletinID, attachmentsFromDraft, setAlertProperty]);

  // When we have tags from draft/query and bulletinID, create each tag via create_tag_request and patch, then update tagDict
  useEffect(() => {
    if (
      !props.bulletinID ||
      !tagsFromDraft ||
      !Array.isArray(tagsFromDraft) ||
      tagsFromDraft.length === 0 ||
      tagsSyncedRef.current
    ) {
      return;
    }
    tagsSyncedRef.current = true;
    const createTags = async () => {
      const draftIds = new Set(tagsFromDraft.map((t) => t.id));
      const newTagDict = { ...alertProperties.tagDict };
      for (const id of draftIds) {
        delete newTagDict[id];
      }
      for (const tag of tagsFromDraft) {
        try {
          const { data: tagData } = await createTagRequest({
            path: { bulletin_id: props.bulletinID },
            body: { title: tag.title },
          });
          const newTag = {
            id: tagData!.id,
            title: tagData!.title ?? tag.title,
            description: tag.description ?? "",
            description_fr: tag.description_fr ?? "",
            attachments: tag.attachments ?? [],
          };
          newTagDict[tagData!.id] = newTag;
          if (
            (tag.description && tag.description !== "") ||
            (tag.attachments && tag.attachments.length > 0)
          ) {
            await patchTagRequest({
              path: { bulletin_id: props.bulletinID, tag_id: tagData!.id },
              body: newTag,
            });
          }
        } catch (err) {
          console.error("Failed to create tag:", tag.title, err);
        }
      }
      setAlertProperty("tagDict", newTagDict);
      setAlertProperty("tagsFromDraft", []);
    };
    createTags().catch(() => {
      tagsSyncedRef.current = false;
    });
  }, [props.bulletinID, tagsFromDraft, setAlertProperty]);

  const handlePdfUpload = (event) => {
    const ext = event.target.value.match(/\.([^.]+)$/)[1];
    switch (ext) {
      case "pdf":
        break;
      default:
        toast.error(t("toast.error.fileTypeNotAllowed"));
        return;
    }
    setIsPDFUploading(true);
    uploadBulletinPdf({
      path: { bulletin_id: props.bulletinID },
      body: { file: event.target.files[0] },
    })
      .then((res) => {
        setAlertProperty("pdf", (res.data as { pdf: IPdfFile }).pdf);
        setIsPDFUploading(false);
      })
      .catch(() => {
        toast.error(t("toast.error.errorUploadingPDF"));
        setIsPDFUploading(false);
      });
  };

  useEffect(() => {
    shouldCancelRef.current = shouldCancel;
  });

  const handleImageUpload = async (event, files) => {
    setIsImageUploading(true);
    // TODO: Add a check for the file type

    for (let index = 0; index < files.length; index++) {
      try {
        const res = await uploadBulletinAttachment({
          path: { bulletin_id: props.bulletinID },
          body: { file: files[index] },
        });

        if (shouldCancelRef.current) {
          // Cancelled uploading, break the loop
          handleFileDelete(files[index].name, props.bulletinID.toString());
          break;
        }

        if (res.data) {
          setAlertProperty("evidence_files", {
            ...((res.data as BulletinResource).evidence_files as {
              [key: string]: IEvidenceFile;
            }),
          });
        } else {
          toast.error(t("toast.error.errorUploadingImage"));
          break;
        }
      } catch (err) {
        toast.error(t("toast.error.errorUploadingImage"));
        break;
      }
    }
    setShouldCancel(false);
    setIsImageUploading(false);

    if (event.target) {
      event.target.value = null;
    }
  };

  const handleFileDelete = (fileName: string, bulletinID: string) => {
    setIsImageUploading(true);
    // Delete from server
    deleteBulletinAttachment({
      path: { bulletin_id: parseInt(bulletinID), filename: fileName },
    })
      .then((res) => {
        // Delete from local state
        if (res.data) {
          setAlertProperty("evidence_files", {
            ...((res.data as BulletinResource).evidence_files as {
              [key: string]: IEvidenceFile;
            }),
          });
        }
        setIsImageUploading(false);
      })
      .catch((err) => {
        setIsImageUploading(false);
        toast.error(t("toast.error.errorDeletingFile"), err);
      });
  };

  const handleFileReupload = (
    fileName: string,
    bulletinID: number,
    file: File,
  ) => {
    setIsImageUploading(true);

    setAlertProperty("evidence_files", {});
    uploadBulletinAttachment({
      path: { bulletin_id: bulletinID },
      body: { file },
    })
      .then((res) => {
        // Update local state
        if (res.data) {
          setAlertProperty("evidence_files", {
            ...((res.data as BulletinResource).evidence_files as {
              [key: string]: IEvidenceFile;
            }),
          });
        }
        setIsImageUploading(false);
      })
      .catch((err) => {
        setIsImageUploading(false);
        toast.error(t("toast.error.errorDeletingFile"), err);
      });
    setEditMediaObject(null);
  };

  return (
    <div style={{ width: "100%" }}>
      {!("filename" in pdf) && (
        <div
          className={styles.alertUploadContainer}
          onClick={() => {
            if (pdfInput.current) {
              pdfInput.current.click();
            }
          }}
        >
          <div className={styles.uploadBtn}>
            <img src={upload} className={styles.uploadBtnImage} alt="" />
            <div className={styles.uploadBtnCaption}>
              {t("uploadBtnCaption")} <br />
              <span>{t("option")}</span>
            </div>
          </div>

          {isPdfUploading && (
            <CircularProgress size="20px" thickness={5} color="secondary" />
          )}
          <input
            ref={pdfInput}
            onChange={handlePdfUpload}
            type="file"
            accept=".pdf"
            style={{ display: "none" }}
          />
        </div>
      )}
      {"filename" in pdf && (
        <div className={styles.alertUploadContainer}>
          <div className={styles.uploadBtnCaption}>BULLETIN</div>
          <div className={styles.uploadedPdfParent}>
            <div className={styles.uploadedPdfName}>{pdf.filename}</div>
            <Tooltip title={t("deleteBtnCaption")} placement="top">
              <IconButton
                onClick={(e) => {
                  deleteBulletinPdf({
                    path: { bulletin_id: props.bulletinID },
                  });
                  setAlertProperty("pdf", {});
                  e.stopPropagation();
                }}
                color="primary"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}
      <div className={styles.alertUploadContainer}>
        {!isImageUploading && (
          <TriUploadFile
            fileInput={imageInput}
            handleFileUpload={handleImageUpload}
            accept="image/*,video/*"
            multiple
          />
        )}
        {isImageUploading && (
          <>
            <CircularProgress size="20px" thickness={5} color="secondary" />
            <Button
              variant="outlined"
              onClick={() => {
                setShouldCancel(true);
                setIsImageUploading(false);
              }}
            >
              {t("cancel")}
            </Button>
          </>
        )}
      </div>

      {mode === "upload" && props.imagesLoading ? (
        <div
          style={{
            padding: "36px",
          }}
        >
          <LoadingModal
            stages={[
              t("stage.extractingImages"),
              t("stage.uploadingImages"),
              t("stage.processingImages"),
              t("stage.generatingSmartSearchTags"),
              t("stage.wrappingUp"),
            ]}
            timingMessage={t("stage.timingMessage")}
          />
        </div>
      ) : null}

      {isAttachmentsSyncing && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 2,
          }}
        >
          <CircularProgress size="20px" thickness={5} color="secondary" />
          <Typography variant="body2" color="text.secondary">
            {t("stage.loadingImagesFromReport")}
          </Typography>
        </Box>
      )}

      {Object.keys(evidence_files).length > 0 && (
        <SortableAttachments
          handleFileDelete={handleFileDelete}
          bulletinID={props.bulletinID}
          attachments={evidence_files}
          setAttachments={setAlertProperty}
          setEditMediaObject={setEditMediaObject}
        />
      )}
      {editMediaObject && (
        <ImageEditor
          imageObject={editMediaObject}
          handleImageUpload={handleFileReupload}
          onCancel={() => {
            setEditMediaObject(null);
          }}
          bulletinID={props.bulletinID}
        />
      )}
      <div className={styles.tagsContainer}>
        <ManageTags bulletinID={props.bulletinID} />
      </div>
    </div>
  );
};
